import type { MaybeN } from "@agrigate/ts-utils/lib/types";
import type {
    DrawerBodyProps,
    DrawerRootProps,
    SystemStyleObject,
} from "@chakra-ui/react";
import { Box, CloseButton, IconButton } from "@chakra-ui/react";
import type { CSSProperties, ReactElement, ReactNode } from "react";
import { LuMaximize2 } from "react-icons/lu";
import { useNavigate } from "react-router";

import {
    DrawerBackdrop,
    DrawerBody,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    DrawerRoot,
} from "./components/ui/drawer";

interface DrawerProps extends Omit<DrawerRootProps, "children"> {}

export interface DrawerComboProps {
    isOpen: boolean;
    onClose: () => void;
    width?: string;
    headerActions?: MaybeN<ReactNode[]>;
    size?: DrawerRootProps["size"];
    title?: ReactNode;
    sx?: SystemStyleObject;
    showHeader?: boolean;
    placement?: DrawerRootProps["placement"];
    drawerFooter?:
        | ReactNode
        | ((p: { isOpen: boolean; onClose: () => void }) => ReactNode);
    body?:
        | ReactNode
        | ((p: { isOpen: boolean; onClose: () => void }) => ReactNode);
    children?:
        | ReactNode
        | ((p: { isOpen: boolean; onClose: () => void }) => ReactNode);
    styles?: {
        overlay?: CSSProperties;
        content?: CSSProperties;
        header?: CSSProperties;
        footer?: CSSProperties;
        body?: CSSProperties;
    };
    drawerProps?: DrawerProps;
    drawerBodyProps?: DrawerBodyProps;
    expandUrl?: string;
}

/**
 * Wrapper to reduce boilerplate around chakra drawer
 */
export default function DrawerCombo(p: DrawerComboProps): ReactElement {
    const navigate = useNavigate();

    return (
        <DrawerRoot
            open={p.isOpen}
            onOpenChange={e => {
                if (!e.open) p.onClose();
            }}
            placement={p.placement ?? "end"}
            size={p.size}
            {...p.drawerProps}
        >
            <DrawerBackdrop style={p.styles?.overlay} />
            <DrawerContent
                width={p.width}
                background="gray.50"
                style={p.styles?.content}
                className="popup-container"
                display="flex"
                flexDirection="column"
                height="100%"
            >
                <DrawerHeader
                    display="flex"
                    p="2"
                    background="white"
                    flexShrink={0}
                    style={{
                        borderBottomWidth: 1,
                        fontSize: 16,
                        fontWeight: "bold",
                        ...p.styles?.header,
                    }}
                >
                    {p.expandUrl && (
                        <IconButton
                            size="sm"
                            variant="ghost"
                            aria-label="Full screen"
                            onClick={() => navigate(p.expandUrl!)}
                        >
                            <LuMaximize2 />
                        </IconButton>
                    )}
                    <span>{p.title}</span>
                    <Box flexGrow={1} />
                    {p.headerActions}
                    <CloseButton
                        size={"sm"}
                        style={{
                            right: 10,
                            position: "absolute",
                        }}
                        onClick={p.onClose}
                    />
                </DrawerHeader>

                {p.body ? (
                    typeof p.body === "function" ? (
                        p.body(p)
                    ) : (
                        p.body
                    )
                ) : (
                    <DrawerBody
                        css={p.sx} // All nested selectors require the use of the ampersand & prefix
                        scrollBehavior={"auto"}
                        px={4}
                        className="scroller"
                        flex={1}
                        display="flex"
                        flexDirection="column"
                        overflow="auto"
                        style={p.styles?.body}
                        {...p.drawerBodyProps}
                    >
                        {typeof p.children === "function"
                            ? p.children(p)
                            : p.children}
                    </DrawerBody>
                )}

                <div className="popup-slot" />
                {p.drawerFooter && (
                    <DrawerFooter
                        background="white"
                        borderTopWidth="1px"
                        borderTopColor="gray.100"
                        p={2}
                        flexShrink={0}
                        style={p.styles?.footer}
                    >
                        {typeof p.drawerFooter === "function"
                            ? p.drawerFooter(p)
                            : p.drawerFooter}
                    </DrawerFooter>
                )}
            </DrawerContent>
        </DrawerRoot>
    );
}
